import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus,
  Phone,
  Mail,
  MapPin,
  MessageSquare,
  User,
  Calendar,
  Clock,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const ACTIVITY_TYPES = [
  { id: 'chamada', label: 'Chamada', icon: Phone, color: 'bg-blue-500' },
  { id: 'email', label: 'Email', icon: Mail, color: 'bg-purple-500' },
  { id: 'visita', label: 'Visita', icon: MapPin, color: 'bg-emerald-500' },
  { id: 'reuniao', label: 'Reunião', icon: User, color: 'bg-amber-500' },
  { id: 'nota', label: 'Nota', icon: MessageSquare, color: 'bg-slate-500' }
];

export default function ActivitiesPage() {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  
  const [form, setForm] = useState({
    activity_type: 'chamada',
    title: '',
    description: '',
    contact_id: '',
    contact_name: '',
    opportunity_id: '',
    duration_minutes: '',
    outcome: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [activeFilter]);

  const loadData = async () => {
    try {
      const params = activeFilter !== 'all' ? `?activity_type=${activeFilter}` : '';
      const [activitiesRes, contactsRes, oppsRes] = await Promise.all([
        api().get(`/activities${params}`),
        api().get('/contacts'),
        api().get('/opportunities')
      ]);
      setActivities(activitiesRes.data);
      setContacts(contactsRes.data);
      setOpportunities(oppsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar atividades');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...form,
      duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null
    };
    
    try {
      await api().post('/activities', data);
      toast.success('Atividade registada!');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao registar atividade');
    }
  };

  const resetForm = () => {
    setForm({
      activity_type: 'chamada',
      title: '',
      description: '',
      contact_id: '',
      contact_name: '',
      opportunity_id: '',
      duration_minutes: '',
      outcome: '',
      notes: ''
    });
  };

  const getActivityIcon = (type) => {
    const actType = ACTIVITY_TYPES.find(a => a.id === type);
    return actType?.icon || MessageSquare;
  };

  const getActivityColor = (type) => {
    const actType = ACTIVITY_TYPES.find(a => a.id === type);
    return actType?.color || 'bg-slate-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <Tabs value={activeFilter} onValueChange={setActiveFilter} className="w-full sm:w-auto">
          <TabsList>
            <TabsTrigger value="all">Todas</TabsTrigger>
            {ACTIVITY_TYPES.map((type) => (
              <TabsTrigger key={type.id} value={type.id} className="hidden sm:inline-flex">
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        
        <Button 
          className="bg-indigo-800 hover:bg-indigo-900"
          onClick={() => { resetForm(); setShowModal(true); }}
          data-testid="new-activity-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Registar Atividade
        </Button>
      </div>

      {/* Activities Timeline */}
      <Card>
        <CardContent className="p-6">
          {activities.length === 0 ? (
            <div className="text-center py-16">
              <Clock className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold font-[Manrope]">Nenhuma atividade registada</h3>
              <p className="text-muted-foreground mt-1">Comece por registar uma chamada, visita ou nota</p>
              <Button 
                className="mt-4 bg-indigo-800 hover:bg-indigo-900"
                onClick={() => { resetForm(); setShowModal(true); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Registar Atividade
              </Button>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />
              
              <div className="space-y-6">
                {activities.map((activity) => {
                  const Icon = getActivityIcon(activity.activity_type);
                  const colorClass = getActivityColor(activity.activity_type);
                  
                  return (
                    <div 
                      key={activity.id} 
                      className="relative flex gap-4"
                      data-testid={`activity-item-${activity.id}`}
                    >
                      <div className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full ${colorClass} flex items-center justify-center text-white`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      
                      <div className="flex-1 bg-white border rounded-lg p-4 hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="font-medium">{activity.title}</h4>
                            {activity.description && (
                              <p className="text-sm text-muted-foreground mt-1">{activity.description}</p>
                            )}
                          </div>
                          <Badge variant="secondary" className="flex-shrink-0">
                            {ACTIVITY_TYPES.find(t => t.id === activity.activity_type)?.label}
                          </Badge>
                        </div>
                        
                        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(activity.created_at), "d MMM yyyy 'às' HH:mm", { locale: pt })}
                          </span>
                          
                          {activity.contact_name && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {activity.contact_name}
                            </span>
                          )}
                          
                          {activity.duration_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {activity.duration_minutes} min
                            </span>
                          )}
                        </div>
                        
                        {activity.outcome && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <p className="text-sm font-medium">Resultado:</p>
                            <p className="text-sm text-muted-foreground">{activity.outcome}</p>
                          </div>
                        )}
                        
                        {activity.notes && (
                          <div className="mt-3 p-3 bg-indigo-50 rounded-lg border-l-4 border-indigo-500">
                            <p className="text-sm">{activity.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Registar Atividade</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Tipo de Atividade</Label>
                <div className="grid grid-cols-5 gap-2">
                  {ACTIVITY_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setForm({ ...form, activity_type: type.id })}
                      className={`
                        flex flex-col items-center gap-1 p-3 rounded-lg border transition-all
                        ${form.activity_type === type.id 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-slate-200 hover:border-slate-300'
                        }
                      `}
                      data-testid={`activity-type-${type.id}`}
                    >
                      <div className={`p-2 rounded-full ${type.color} text-white`}>
                        <type.icon className="h-4 w-4" />
                      </div>
                      <span className="text-xs">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  data-testid="activity-title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact">Contacto</Label>
                  <Select 
                    value={form.contact_id} 
                    onValueChange={(v) => {
                      const contact = contacts.find(c => c.id === v);
                      setForm({ ...form, contact_id: v, contact_name: contact?.name || '' });
                    }}
                  >
                    <SelectTrigger data-testid="activity-contact-select">
                      <SelectValue placeholder="Selecionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {contacts.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duração (min)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="0"
                    value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                    data-testid="activity-duration-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="activity-description-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="outcome">Resultado</Label>
                <Input
                  id="outcome"
                  value={form.outcome}
                  onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                  placeholder="Ex: Cliente interessado, agendar reunião"
                  data-testid="activity-outcome-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="activity-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-indigo-800 hover:bg-indigo-900" data-testid="save-activity-btn">
                Registar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
